import { isNotNullish } from '@adornis/assert/isNullish.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { DialogHeader } from '@adornis/components/client/DialogHeader.js';
import { ErrorState } from '@adornis/components/client/ErrorState.js';
import { formatCalendarDay, toCalendarDay, todayAsCalendarDay } from '@adornis/datetime/calendarDay.js';
import { guardDialog, showDialog } from '@adornis/dialog/dialog-functions.js';
import { DateInput } from '@adornis/formfield/fields/date-input.js';
import { ToggleInput } from '@adornis/formfield/fields/toggle-input.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import type { Validator } from '@adornis/validation/validation.js';
import type { ValidationFunctionOptions } from '@adornis/validation/validator.js';
import { endOfMonth } from 'date-fns';
import { html, nothing } from 'lit';
import type { ObservedValueOf } from 'rxjs';
import { formatBillingPositionPrice } from '../../billingPositions/client/BillingPositionPriceInput.js';
import { BillingRhythm, translateBillingRhythm } from '../../billingPositions/entities/BillingPosition.js';
import { BookingAPI } from '../../bookings/api/BookingAPI.js';
import { ClassBooking } from '../../bookings/entities/ClassBooking.js';
import { EmployeeBooking } from '../../bookings/entities/EmployeeBooking.js';
import { hasRoles } from '../../permissions/api/hasRoles.js';
import { Role } from '../../permissions/roles.js';
import { getSchoolYearIntervalDatesFromDate } from '../../schoolyear/getSchoolYear.js';
import { SepaAPI } from '../../sepa/api/SepaAPI.js';
import { Person } from '../../users/entities/person.js';
import { UserListSelectionInterface } from '../../users/list/entities/UserListSelectionInterface.js';
import { AustrittAPI } from '../api/AustrittAPI.js';
import { getExitDefault, getMandateEndOnExit, isEndableOnExit, isRemovedOnExit } from '../api/planStudentExit.js';
import { StudentExitOptions } from '../entities/StudentExitOptions.js';

const day = (date: Maybe<Date>) => (date ? formatCalendarDay(date, 'dd.MM.yyyy') : 'offen');
const runtime = (start: Maybe<Date>, end: Maybe<Date>) => `${day(start)} – ${day(end)}`;

const StudentExitFormValidator = {
  endDate: (options: ValidationFunctionOptions<Maybe<Date>, StudentExitOptions>) => {
    if (!options.value)
      throw new ValidationError('Bitte ein Austrittsdatum wählen.', { key: options.key, path: options.path });
  },
} satisfies Validator;

const loadPerson = (personID: string) =>
  subscribeByID(
    Person,
    personID,
  )({ _id: 1, roles: { name: 1, contexts: 1 }, profilDaten: { stammdaten: { vorname: 1, nachname: 1 } } });

const loadBookings = (personID: string) =>
  BookingAPI.subscribeForSelection(new UserListSelectionInterface({ userIDs: [personID] }))({
    _id: 1,
    _class: 1,
    groupIDs: 1,
    groups: { _id: 1, name: 1, billingPosition: { priceInCents: 1, rhythm: 1 } },
    startDate: 1,
    endDate: 1,
    bookingReference: 1,
  });

const loadMandates = (bookingIDs: string[]) =>
  SepaAPI.subscribeForBookings(bookingIDs)({
    _id: 1,
    bookingID: 1,
    accepted: 1,
    name: 1,
    mandateReference: 1,
    acceptedPersonName: 1,
    amountInCents: 1,
    canEditAmount: 1,
    rhythm: 1,
    start: 1,
    end: 1,
    billedOn: 1,
  });

type ExitPerson = NonNullable<ObservedValueOf<ReturnType<typeof loadPerson>>>;
type ExitBookings = ObservedValueOf<ReturnType<typeof loadBookings>>;
type ExitMandates = ObservedValueOf<ReturnType<typeof loadMandates>>;

const AustrittForm = stateful(
  (props: {
    person: ExitPerson;
    bookings: ExitBookings;
    mandates: ExitMandates;
    resolve: (ended: number) => void;
    reject: () => void;
  }) => {
    const today = todayAsCalendarDay();
    // the rules only pick where the switches start; set once, so a live update never flips a switch back
    const [options] = useState(
      new StudentExitOptions({
        personID: props.person._id,
        endDate: today,
        bookingIDs: props.bookings
          .filter(booking => booking._class !== EmployeeBooking._class && getExitDefault(booking, props.mandates).ends)
          .map(booking => booking._id),
      }),
      [],
    );
    const form = useForm(options, { validator: StudentExitFormValidator });
    const endDate = form.document.endDate;
    const switchedOn = new Set(form.document.bookingIDs ?? []);

    const isStudent = hasRoles({ user: props.person, roles: [Role.SCHUELER] });
    const endable = endDate ? props.bookings.filter(booking => isEndableOnExit(booking, endDate)) : [];
    const ending = endable.filter(booking => switchedOn.has(booking._id));
    const staying = endable.filter(booking => !switchedOn.has(booking._id));
    const removing = endDate ? ending.filter(booking => isRemovedOnExit(booking, endDate)) : [];
    const removingIDs = new Set(removing.map(booking => booking._id));
    const endingIDs = new Set(ending.map(booking => booking._id));
    const mandateChanges = props.mandates
      .filter(mandate => mandate.accepted)
      .map(mandate => ({
        mandate,
        deleted: removingIDs.has(mandate.bookingID),
        newEnd:
          endDate && endingIDs.has(mandate.bookingID) && !removingIDs.has(mandate.bookingID)
            ? getMandateEndOnExit(mandate, endDate)
            : null,
      }));

    const setSwitch = (bookingID: string, on: boolean) =>
      form
        .field('bookingIDs')
        .onPicked?.(on ? [...switchedOn, bookingID] : [...switchedOn].filter(id => id !== bookingID));
    const setEndDate = (date: Date | null) => form.field('endDate').onPicked?.(date);
    const schoolYearEnd = getSchoolYearIntervalDatesFromDate(today).end;
    const bookingName = (booking: ExitBookings[number]) =>
      booking.groups?.map(group => group.name).join(' · ') || booking.bookingReference || 'Mitgliedschaft';
    const openInEditor = (bookingID: string) => {
      props.reject();
      goTo(`/bookings?selection=${JSON.stringify({ userIDs: [props.person._id] })}&openBooking=${bookingID}`);
    };

    const handleConfirm = async () => {
      if (await form.validate()) return;
      // only what changes on this date - exactly what the tables show
      const exit = new StudentExitOptions({
        personID: props.person._id,
        endDate,
        bookingIDs: ending.map(booking => booking._id),
      });
      await guardDialog(() => AustrittAPI.execute(exit)(), { success: ended => props.resolve(ended) });
    };

    const openOneTime = mandateChanges.filter(
      ({ mandate, newEnd }) => newEnd && mandate.rhythm === BillingRhythm.ONCE_CUSTOM && !mandate.billedOn,
    );
    const classBookingsStaying = staying.filter(booking => booking._class === ClassBooking._class);

    return html`
      <div class="dialog-container flex flex-col gap-4">
        ${DialogHeader({
          title: `Austritt · ${props.person.profilDaten?.stammdaten?.vorname ?? ''} ${props.person.profilDaten?.stammdaten?.nachname ?? ''}`,
          close: props.reject,
          closeLabel: 'Dialog schließen',
        })}

        <div class="flex flex-col gap-1">
          <span class="text-sm font-semibold text-base-content/70">Austrittsdatum (letzter Tag)</span>
          <div class="flex flex-wrap items-center gap-2">
            <div>${DateInput({ ...form.field('endDate') })}</div>
            <button class="btn btn-sm btn-outline" @click=${() => setEndDate(today)}>Heute</button>
            <button class="btn btn-sm btn-outline" @click=${() => setEndDate(toCalendarDay(endOfMonth(today)))}>
              Monatsende
            </button>
            ${
              schoolYearEnd
                ? html`<button class="btn btn-sm btn-outline" @click=${() => setEndDate(schoolYearEnd)}>
                    Schuljahresende
                  </button>`
                : nothing
            }
          </div>
        </div>

        <div class="flex flex-col gap-2">
          ${
            isStudent
              ? nothing
              : html`<div role="alert" class="alert alert-error alert-soft">
                  Der Austrittsassistent ist derzeit nur für aktive Schüler verfügbar.
                </div>`
          }
          ${
            endDate && endDate < today
              ? html`<div role="alert" class="alert alert-warning alert-soft">
                  Das Datum liegt in der Vergangenheit. Bereits eingezogene Beträge nach dem ${day(endDate)} werden
                  nicht erstattet.
                </div>`
              : nothing
          }
          ${openOneTime.map(
            ({ mandate }) =>
              html`<div role="alert" class="alert alert-warning alert-soft">
                ${mandate.name} (${formatBillingPositionPrice(mandate.amountInCents)}) wurde noch nicht eingezogen.
                Endet die Mitgliedschaft, wird das Mandat nicht mehr eingezogen.
              </div>`,
          )}
          ${removing.map(
            booking =>
              html`<div role="alert" class="alert alert-warning alert-soft">
                ${bookingName(booking)} beginnt erst in einem späteren Schuljahr. Eine Klassenmitgliedschaft kann nicht
                vor ihrem Schuljahr enden, deshalb wird sie mit ihren SEPA-Mandaten gelöscht.
              </div>`,
          )}
          ${
            isStudent && classBookingsStaying.length
              ? html`<div role="alert" class="alert alert-warning alert-soft">
                  ${classBookingsStaying.map(bookingName).join(', ')} bleibt bestehen. Die Person bleibt deshalb Schüler
                  oder wird es mit Beginn der Mitgliedschaft wieder.
                </div>`
              : nothing
          }
          ${
            staying.length
              ? html`<div role="alert" class="alert alert-info alert-soft">
                  ${staying.length} ${staying.length === 1 ? 'Mitgliedschaft bleibt' : 'Mitgliedschaften bleiben'} offen
                  und ${staying.length === 1 ? 'muss' : 'müssen'} später manuell beendet werden.
                </div>`
              : nothing
          }
        </div>

        <div class="flex flex-col gap-2">
          <h3 class="font-semibold">Mitgliedschaften</h3>
          ${
            props.bookings.length
              ? html`<div class="overflow-x-auto rounded-box border border-base-300">
                  <table class="table table-sm">
                    <thead>
                      <tr>
                        <th>Beenden</th>
                        <th>Mitgliedschaft</th>
                        <th>Bisher</th>
                        <th>Danach</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${props.bookings.map(booking => {
                        const rule = getExitDefault(booking, props.mandates);
                        const canEnd = endable.includes(booking);
                        const on = switchedOn.has(booking._id);
                        return html`
                          <tr class=${canEnd ? '' : 'text-base-content/50'}>
                            <td>
                              ${canEnd ? ToggleInput({ value: on, onPicked: value => setSwitch(booking._id, !!value) }) : nothing}
                            </td>
                            <td>
                              <div>${bookingName(booking)}</div>
                              <div class="text-xs text-base-content/60">
                                ${
                                  canEnd
                                    ? `Standard ${rule.ends ? 'an' : 'aus'}: ${rule.reason}${on !== rule.ends ? ' · geändert' : ''}`
                                    : 'Wird nicht verändert'
                                }
                              </div>
                            </td>
                            <td class="whitespace-nowrap">${runtime(booking.startDate, booking.endDate)}</td>
                            <td class="whitespace-nowrap">
                              ${
                                canEnd && on
                                  ? html`<span class="font-semibold text-error">
                                      ${removingIDs.has(booking._id) ? 'wird gelöscht' : runtime(booking.startDate, endDate)}
                                    </span>`
                                  : runtime(booking.startDate, booking.endDate)
                              }
                              ${
                                canEnd && !on
                                  ? html`<button
                                      class="btn btn-link btn-xs px-0"
                                      @click=${() => openInEditor(booking._id)}
                                    >
                                      Im Editor öffnen
                                    </button>`
                                  : nothing
                              }
                            </td>
                          </tr>
                        `;
                      })}
                    </tbody>
                  </table>
                </div>`
              : html`<p class="text-sm text-base-content/70">Die Person hat keine Mitgliedschaften.</p>`
          }
        </div>

        <div class="flex flex-col gap-2">
          <h3 class="font-semibold">SEPA-Mandate</h3>
          ${
            mandateChanges.length
              ? html`<div class="overflow-x-auto rounded-box border border-base-300">
                  <table class="table table-sm">
                    <thead>
                      <tr>
                        <th>Mandat</th>
                        <th>Bisher</th>
                        <th>Danach</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${mandateChanges.map(
                        ({ mandate, newEnd, deleted }) => html`
                          <tr class=${newEnd || deleted ? '' : 'text-base-content/50'}>
                            <td>
                              <div>
                                ${mandate.name ?? 'Ohne Mandatsname'} ·
                                ${formatBillingPositionPrice(mandate.amountInCents)}
                                ${mandate.rhythm ? translateBillingRhythm(mandate.rhythm) : nothing}
                              </div>
                              <div class="text-xs text-base-content/60">
                                ${mandate.mandateReference ?? 'Ohne Mandatsreferenz'} · ${mandate.acceptedPersonName}
                              </div>
                            </td>
                            <td class="whitespace-nowrap">${runtime(mandate.start, mandate.end)}</td>
                            <td class="whitespace-nowrap">
                              ${
                                deleted
                                  ? html`<span class="font-semibold text-error">wird gelöscht</span>`
                                  : newEnd
                                    ? html`<span class="font-semibold text-error"
                                        >${runtime(mandate.start, newEnd)}</span
                                      >`
                                    : 'unverändert'
                              }
                            </td>
                          </tr>
                        `,
                      )}
                    </tbody>
                  </table>
                </div>`
              : html`<p class="text-sm text-base-content/70">Keine akzeptierten SEPA-Mandate.</p>`
          }
        </div>

        <div class="flex flex-wrap items-center justify-between gap-2">
          <span class="text-sm text-base-content/70">
            ${ending.length - removing.length} Mitgliedschaften und
            ${mandateChanges.filter(({ newEnd }) => newEnd).length} Mandate enden
            ${endDate ? `zum ${day(endDate)}` : ''}${removing.length ? `, ${removing.length} Mitgliedschaften werden gelöscht` : ''}
          </span>
          <div class="flex gap-2">
            <button class="btn btn-outline" @click=${props.reject}>Abbrechen</button>
            <button class="btn btn-error" ?disabled=${!isStudent || !ending.length} @click=${handleConfirm}>
              Austritt ${endDate ? `zum ${day(endDate)} ` : ''}durchführen
            </button>
          </div>
        </div>
      </div>
    `;
  },
);

const AustrittDialogContent = stateful(
  (props: { personID: string; resolve: (ended: number) => void; reject: () => void }) => {
    const person = useQuery(() => loadPerson(props.personID), [props.personID]);
    const bookings = useQuery(() => loadBookings(props.personID), [props.personID]);
    const bookingIDs = (bookings.value ?? []).map(booking => booking._id).filter(isNotNullish);
    const mandates = useQuery(
      () => (bookingIDs.length ? loadMandates(bookingIDs) : Promise.resolve([])),
      [bookingIDs.join(',')],
    );

    const error = person.error ?? bookings.error ?? mandates.error;
    if (error)
      return html`<div class="dialog-container">
        ${DialogHeader({ title: 'Austritt', close: props.reject, closeLabel: 'Dialog schließen' })}
        ${ErrorState({ title: 'Laden fehlgeschlagen', description: error.message })}
      </div>`;

    if (person.loading || bookings.loading || mandates.loading || !bookings.value || !mandates.value)
      return html`<div class="dialog-container flex justify-center p-8">
        <span class="loading loading-spinner loading-md"></span>
      </div>`;

    if (!person.value)
      return html`<div class="dialog-container">
        ${DialogHeader({ title: 'Austritt', close: props.reject, closeLabel: 'Dialog schließen' })}
        ${ErrorState({ title: 'Person nicht gefunden' })}
      </div>`;

    return AustrittForm({
      person: person.value,
      bookings: bookings.value,
      mandates: mandates.value,
      resolve: props.resolve,
      reject: props.reject,
    });
  },
);

/**
 * Opens the Austrittsassistent for one student. Resolves to the number of bookings ended, or null
 * when the dialog is closed without an Austritt.
 */
export async function openAustrittDialog(personID: string): Promise<number | null> {
  try {
    return await showDialog<number>({
      content: (resolve, reject) => AustrittDialogContent({ personID, resolve, reject }),
      modal: true,
      modalBoxClasses: 'max-w-4xl',
    });
  } catch {
    // dialog was dismissed
    return null;
  }
}
